import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { ApiError } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { Button, Field } from "../components/Ui.tsx";

/**
 * Girişte 401 "oturumun düştü" değil, "bilgiler hatalı" demektir — genel çevirici
 * bunu bilemez; sunucunun Türkçe mesajı olduğu gibi gösterilir.
 */
function loginError(err: unknown): string {
  if (err instanceof ApiError && err.status === 401) return err.message || "E-posta veya parola hatalı";
  // Sunucu JSON yerine hata sayfası döndürdüyse (502 vb.) ayrıştırma patlar.
  if (err instanceof SyntaxError) return "Sunucu şu an yanıt veremiyor. Birazdan tekrar dene.";
  return humanizeError(err, "Giriş başarısız");
}

export function LoginPage(): ReactNode {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function login(code: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await client.login({
        email,
        password,
        ...(code ? { totp: code } : {}),
        deviceName: navigator.userAgent.slice(0, 60),
      });
      location.href = "/";
    } catch (err) {
      if (err instanceof ApiError && err.code === "TOTP_REQUIRED") {
        setNeedsTotp(true);
        setError(null);
      } else {
        setError(loginError(err));
        // Yanlış kod: alanı boşalt ki yeni kod baştan yazılsın (ve yeniden otomatik gönderilsin).
        if (needsTotp) setTotp("");
      }
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (busy) return;
    void login(totp);
  }

  return (
    <div className="grid min-h-full place-items-center px-4 py-10">
      <form
        onSubmit={submit}
        className="card grid w-full max-w-[340px] gap-3.5 px-6 py-6"
        aria-busy={busy || undefined}
      >
        <div className="grid gap-1">
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold">GWR</span>
            <span className="text-lg font-medium text-accent-400">Control</span>
          </div>
          <span className="text-xs text-neutral-500">Uygulama kontrol merkezi</span>
        </div>

        <Field label="E-posta">
          <input
            className="input"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </Field>

        <Field label="Parola">
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>

        {needsTotp && (
          <Field label="Doğrulama kodu" hint="Authenticator'daki 6 haneli kod; 6. hanede kendiliğinden gönderilir.">
            <input
              className="input mono text-center text-md tracking-[0.18em]"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totp}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                setTotp(v);
                if (v.length === 6 && !busy) void login(v);
              }}
              maxLength={6}
              autoFocus
            />
          </Field>
        )}

        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}

        <Button variant="primary" className="w-full" loading={busy} type="submit">
          Giriş
        </Button>

        <span className="text-2xs text-pretty text-neutral-600">
          Yeni cihaz kaydı denetim kaydına yazılır. Parola + TOTP zorunlu.
        </span>
      </form>
    </div>
  );
}
